// WHAT: Hook for fetching correction factor data from Supabase
// WHY:  Provides interface for loading TDEE correction factor calculations with caching
// DOMAIN: Core → Dashboard
// DEPENDENCIES: getSupabaseClient from shared services
// RISKS:
//   - NOTE: Fetches only latest entry - may not reflect current state
//   - NOTE: Error handling is generic - may not provide detailed feedback
//   - NOTE: No automatic refresh mechanism for stale data
//   - NOTE: Console.error should be replaced with proper logging

import { useState, useEffect, useCallback } from "react";

import { debugLog } from "@/domains/logging/utils/debugLog";
import { getSupabaseClient } from "@/shared/services";

/**
 * Correction factor data interface
 */
export interface CorrectionFactorData {
  /** Confidence level */
  confidence: string;
  /** Calculation date */
  date: string;
  /** Weight change */
  delta_kg: number;
  /** Energy stored */
  energy_stored: number;
  /** Calorie intake */
  intake: number;
  /** Oura correction factor */
  oura_factor: number;
  /** Oura TDEE estimate */
  oura_tdee: number;
  /** Calculated real TDEE */
  real_tdee: number;
  /** Warning message */
  warning: string;
  /** Current weight */
  weight_now: number;
  /** Starting weight */
  weight_start: number;
  /** Time window in days */
  window_days: number;
}

/**
 * Hook return type
 */
export interface UseCorrectionFactorReturn {
  /** Correction factor data */
  data: CorrectionFactorData | null;
  /** Error message */
  error: null | string;
  /** Loading state */
  loading: boolean;
  /** Refetch function */
  refetch: () => Promise<void>;
}

/**
 * WHAT:
 *   Hook that provides interface for fetching correction factor data from
 *   Supabase with automatic loading and refetch capabilities.
 *
 * WHY:
 *   Centralizes correction factor data management and provides consistent
 *   interface for components.
 *
 * BEHAVIOR:
 *   - Auto-loads latest entry on mount
 *   - Fetches most recent correction factor calculation
 *   - Manages loading and error states
 *   - Provides refetch functionality
 *
 * RISKS:
 *   - NOTE: Fetches only latest entry - may not reflect current state
 *   - NOTE: Error handling is generic - may not provide detailed feedback
 *   - NOTE: No automatic refresh mechanism for stale data
 *   - NOTE: Console.error should be replaced with proper logging
 *
 * @returns {UseCorrectionFactorReturn} Hook return object with data, error, loading state, and refetch function
 *
 */
export const useCorrectionFactor = (): UseCorrectionFactorReturn => {
  const [data, setData] = useState<CorrectionFactorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const supabase = await getSupabaseClient();
      const result = await supabase
        .from("correction_factor")
        .select("*")
        .order("date", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.data) {
        setData(result.data as CorrectionFactorData);
      } else {
        setData(null);
      }
    } catch (error_) {
      const errorMessage =
        error_ instanceof Error ? error_.message : "Failed to fetch correction factor data";
      setError(errorMessage);
      debugLog("error", "core", "Error fetching correction factor", {
        error: errorMessage,
        file: "useCorrectionFactor.ts",
        function: "fetchData",
        module: "dashboard",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const refetch = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return {
    data,
    error,
    loading,
    refetch,
  };
};
